import clsx from 'clsx';
import React, { ReactElement, useState } from 'react';
import { LinkedinShareButton, TwitterShareButton } from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FaLinkedin } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { MdEmail } from 'react-icons/md';
import { HiClipboardCopy, HiCheck } from 'react-icons/hi';
import { Tooltip } from '../Tooltip/Tooltip';

export type ShareButtonsProps = {
  title: string;
  summary: string;
  shareUrl: string;
  channels?: ('linkedin' | 'x' | 'email' | 'copy')[];
};

const iconSize = '16px';
const iconBorder = `rounded-full border border-action-outline-secondary-enabled text-content-secondary p-2`;

const linkedInShareLabel = 'Share on LinkedIn';
const XShareLabel = 'Share on X';
const emailShareLabel = 'Share via Email';
const copyLabel = 'Copy URL to clipboard';

export const ShareButtons: React.ElementType<ShareButtonsProps> = ({
  shareUrl,
  title,
  summary,
  channels = ['linkedin', 'x', 'email', 'copy'],
}): ReactElement<any> => {
  const [copied, setCopied] = useState(false);
  const handleCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex space-x-1 leading-none">
      {channels.includes('linkedin') && (
        <LinkedinShareButton
          url={shareUrl}
          title={title}
          summary={summary}
          source="IN-PART"
          aria-label={linkedInShareLabel}
          translate="yes"
        >
          <Tooltip label={linkedInShareLabel}>
            <div className={clsx(iconBorder, `hover:text-color-[#0072b1]`)}>
              <FaLinkedin size={iconSize} aria-hidden />
            </div>
          </Tooltip>
        </LinkedinShareButton>
      )}

      {channels.includes('x') && (
        <TwitterShareButton translate="yes" url={shareUrl} title={title} aria-label={XShareLabel}>
          <Tooltip label={XShareLabel}>
            <div className={clsx(iconBorder, `hover:text-color-[#00acee]`)}>
              <FaXTwitter size={iconSize} aria-hidden />
            </div>
          </Tooltip>
        </TwitterShareButton>
      )}

      {channels.includes('email') && (
        <a
          className="inline-block"
          target="_blank"
          rel="noreferrer noopener"
          href={`mailto:?subject=${title}&body=${encodeURIComponent(shareUrl)}`}
          aria-label={emailShareLabel}
        >
          <Tooltip label={emailShareLabel}>
            <div className={clsx(iconBorder, `hover:text-primary-600`)}>
              <MdEmail size={iconSize} aria-hidden />
            </div>
          </Tooltip>
        </a>
      )}

      {channels.includes('copy') && (
        <Tooltip label={copyLabel}>
          <button>
            {/* @ts-ignore */}
            <CopyToClipboard text={shareUrl} onCopy={handleCopied}>
              <div className={clsx(iconBorder, `inline-block hover:text-primary-600`)} aria-label={copyLabel}>
                {copied ? <HiCheck size={iconSize} aria-hidden /> : <HiClipboardCopy size={iconSize} aria-hidden />}
              </div>
            </CopyToClipboard>
          </button>
        </Tooltip>
      )}
    </div>
  );
};
